// Map number x from range [a, b] to [c, d]
const map = (x, a, b, c, d) => (x - a) * (d - c) / (b - a) + c;


// Linear interpolation
const lerp = (a, b, n) => (1 - n) * a + n * b;


const clamp = (num, min, max) => num <= min ? min : num >= max ? max : num;


const calcWinsize = () => {
	return {width: window.innerWidth, height: window.innerHeight};
};


// Gets the mouse position
const getMousePos = e => {
	return {
		x: e.clientX,
		y: e.clientY
	};
};


const distance = (x1, y1, x2, y2) => {
	let a = x1 - x2;
	let b = y1 - y2;

	return Math.hypot(a, b);
}


// Generate a random float.
const getRandomFloat = (min, max) => (Math.random() * (max - min) + min).toFixed(2);

export {
	map,
	lerp,
	clamp,
	calcWinsize,
	getMousePos,
	distance,
	getRandomFloat
};


export let utils = {
	/**
	 * Get all siblings of an element
	 * (c) 2021 Chris Ferdinandi, MIT License, https://gomakethings.com
	 * @param  {Node}  el The element
	 * @return {Array}    The siblings
	 */
	getSiblings: (el) => {
		return Array.from(el.parentNode.children).filter(function (sibling) {
			return sibling !== el;
		});
	},

	getPreviousSiblings: (elem, filter) => {
		let sibs = [];
		while (elem = elem.previousSibling)
		{
			if (elem.nodeType === 3)
			{
				continue;
			} // ignore text nodes
			if (!filter || filter(elem))
			{
				sibs.push(elem);
			}
		}
		return sibs;
	},

	getNextSiblings: (elem, filter) => {
		let sibs     = [];
		let nextElem = elem.parentNode.firstChild;
		do
		{
			if (nextElem.nodeType === 3)
			{
				continue;
			} // ignore text nodes
			if (nextElem === elem)
			{
				continue;
			} // ignore elem of target
			if (nextElem === elem.nextElementSibling)
			{
				if (!filter || filter(elem))
				{
					sibs.push(nextElem);
					elem = nextElem;
				}
			}
		} while (nextElem = nextElem.nextSibling)
		return sibs;
	},

	/**
	 * Get the first previous sibling that matches the selector
	 * Usage: utils.getPreviousSibling(item, ':not(.class-to-skip)')
	 * https://gomakethings.com/finding-the-next-and-previous-sibling-elements-that-match-a-selector-with-vanilla-js/
	 */
	getPreviousSibling: (el, selector) => {
		// Get the next sibling element
		let sibling = el.previousElementSibling;

		// If there's no selector, return the first sibling
		if (!selector)
		{
			return sibling;
		}

		// If the sibling matches our selector, use it
		// If not, jump to the next sibling and continue the loop
		while (sibling)
		{
			if (sibling.matches(selector))
			{
				return sibling;
			}
			sibling = sibling.previousElementSibling;
		}
		return null;
	},

	/**
	 * Get the first next sibling that matches the selector
	 * Usage: utils.getNextSibling(item, ':not(.class-to-skip)')
	 * https://gomakethings.com/finding-the-next-and-previous-sibling-elements-that-match-a-selector-with-vanilla-js/
	 */
	getNextSibling: (el, selector) => {
		// Get the next sibling element
		let sibling = el.nextElementSibling;

		// If there's no selector, return the first sibling
		if (!selector)
		{
			return sibling;
		}

		// If the sibling matches our selector, use it
		// If not, jump to the next sibling and continue the loop
		while (sibling)
		{
			if (sibling.matches(selector))
			{
				return sibling;
			}
			sibling = sibling.nextElementSibling
		}
		return null;
	},

	parents: (el, selector) => {
		const parents = [];
		while ((el = el.parentNode) && el !== document)
		{
			if (!selector || el.matches(selector))
			{
				parents.push(el);
			}
		}
		return parents;
	},


	duplicateChildNodes: (el) => {
		let children = el.childNodes;

		children.forEach(function (item) {
			let clonedNode = item.cloneNode(true);
			el.appendChild(clonedNode);
		});
	},


	slideUp: (target, duration = 500) => {
		if (!target.dataset.display)
		{
			target.dataset.display = window.getComputedStyle(target).display;
		}

		let height = target.offsetHeight;

		target.style.transitionProperty = 'height, margin, padding';
		target.style.transitionDuration = duration + 'ms';
		target.style.height             = height + 'px';
		target.offsetHeight;
		target.style.overflow      = 'hidden';
		target.style.height        = 0;
		target.style.paddingTop    = 0;
		target.style.paddingBottom = 0;
		target.style.marginTop     = 0;
		target.style.marginBottom  = 0;

		window.setTimeout(() => {
			target.style.display = 'none';
			target.style.removeProperty('height');
			target.style.removeProperty('padding-top');
			target.style.removeProperty('padding-bottom');
			target.style.removeProperty('margin-top');
			target.style.removeProperty('margin-bottom');
			target.style.removeProperty('overflow');
			target.style.removeProperty('transition-duration');
			target.style.removeProperty('transition-property');
		}, duration);
	},


	slideDown: (target, duration = 500) => {
		const display        = target.dataset.display || 'block';
		target.style.display = display;

		let height = target.offsetHeight;

		target.style.overflow      = 'hidden';
		target.style.height        = 0;
		target.style.paddingTop    = 0;
		target.style.paddingBottom = 0;
		target.style.marginTop     = 0;
		target.style.marginBottom  = 0;
		target.offsetHeight;
		target.style.transitionProperty = 'height, margin, padding';
		target.style.transitionDuration = duration + 'ms';
		target.style.height             = height + 'px';

		target.style.removeProperty('padding-top');
		target.style.removeProperty('padding-bottom');
		target.style.removeProperty('margin-top');
		target.style.removeProperty('margin-bottom');

		window.setTimeout(() => {
			target.style.removeProperty('height');
			target.style.removeProperty('overflow');
			target.style.removeProperty('transition-duration');
			target.style.removeProperty('transition-property');
		}, duration);
	},


	slideToggle: (target, duration = 500) => {

		if (window.getComputedStyle(target).display === 'none')
		{
			utils.slideDown(target, duration);
		}
		else
		{
			utils.slideUp(target, duration);
		}
	},

	toggleClasses: (el, ...cssClasses) => {
		cssClasses.map(cssClass => el.classList.toggle(cssClass))
	},

	getOffset: (el) => {
		const box = el.getBoundingClientRect();

		return {
			top:  box.top + window.scrollY - document.documentElement.clientTop,
			left: box.left + window.scrollX - document.documentElement.clientLeft
		};
	},

	//https://dev.to/btopro/simple-wrap-unwrap-methods-explained-3k5f

	/**
	Usage:
	const el = document.querySelector('table');
	const wrapper = document.createElement('div');
	wrapper.classList.add('table-wrapper');

	utils.wrap(el, wrapper);
	 */
	wrap: (el, wrapper) => {
		if (el && el.parentNode)
		{
			el.parentNode.insertBefore(wrapper, el);
			wrapper.appendChild(el);
		}
	},

	/**
	Usage:
	const el1 = document.querySelector('.el1');
	const el2 = document.querySelector('.el2');
	const wrapper = document.createElement('div');
	wrapper.setAttribute('id', 'mobile-nav-footer');

	utils.wrapAll([el1, el2], wrapper);
	 */
	wrapAll: (elements, wrapper) => {

		if (elements && elements.length)
		{
			elements[0].parentNode.insertBefore(wrapper, elements[0]);

			for (let i in elements)
			{
				wrapper.appendChild(elements[i]);
			}
		}
	},

	/**
	 * Usage:
	 * const wrapper = document.querySelector('.el1');
	 * utils.unwrap(wrapper);
	 */
	unwrap: (el) => {
		if (el && el.parentNode)
		{
			// move all children out of the element
			while (el.firstChild)
			{
				el.parentNode.insertBefore(el.firstChild, el);
			}

			// remove the empty element
			el.remove();
		}
	},

	prev: (el, selector) => {
		const prevEl = el.previousElementSibling;
		if (!selector || (prevEl && prevEl.matches(selector)))
		{
			return prevEl;
		}
		return null;
	},

	next: (el, selector) => {
		const nextEl = el.nextElementSibling;
		if (!selector || (nextEl && nextEl.matches(selector)))
		{
			return nextEl;
		}
		return null;
	},

	serializeForm: (form) => {
		return new URLSearchParams(new FormData(form)).toString();
	}
}


export const randomNumber = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;


/**
 * Transform vh and vw units in pixels
 */
export const vh = (coef) => window.innerHeight * (coef / 100);
export const vw = (coef) => window.innerWidth * (coef / 100);


/**
 * Call whichSizeVideo for each responsive video in the container
 * @param container
 */
export function startWhichSizeVideo(container) {

	const responsiveVideos = container.querySelectorAll('.responsive-video');

	responsiveVideos.forEach((element) => {
		whichSizeVideo(element);
	});
}

/**
 * Load the video matching the media query
 */
export function whichSizeVideo(video) {

	let sources         = video.getElementsByTagName('source');
	let matchingSources = {};
	let src;

	// get all videos matching the media query
	if (sources.length > 0)
	{
		for (let source of sources)
		{
			let mediaQuery = source.dataset.mediaQuery;

			if (matchMedia(mediaQuery).matches)
			{
				matchingSources[source.type] = source.dataset.src
			}
		}
	}

	if (Object.keys(matchingSources).length > 0)
	{
		// use mp4 if the browser is Safari or if there's no webm available
		Object.keys(matchingSources).forEach(function (key, index) {

			if (((is_safari || is_instagram) && ((typeof matchingSources['video/mp4'] !== 'undefined') || (typeof matchingSources['video/mp4; codecs=hvc1'] !== 'undefined'))) || typeof matchingSources['video/webm'] === 'undefined')
			{
				if (typeof matchingSources['video/mp4; codecs=hvc1'] !== 'undefined')
				{
					src = matchingSources['video/mp4; codecs=hvc1'];
				}
				else
				{
					src = matchingSources['video/mp4'];
				}
			}
			else
			{
				src = matchingSources['video/webm'];
			}

			// if the video doesn't have a poster
			// add #t=0.001 to the url to force Safari on iOS to load the first frame of the video
			if (video.poster === '')
			{
				src += '#t=0.001'
			}

			//source.src = src;
			video.src = src;

			// force autoplay after swup changes the page content
			if (video.autoplay)
			{
				let playPromise = video.play();

				if (playPromise !== undefined)
				{
					playPromise.then(_ => {
						// Automatic playback started!
						// Show playing UI.
					})
						.catch(error => {
							// Autoplay was prevented
							// Show paused UI.
						});
				}
			}
		});
	}

	const posterDesktop = video.dataset.posterDesktop;
	const posterMobile  = video.dataset.posterMobile;

	if (posterDesktop !== null && posterMobile !== null && posterDesktop !== undefined && posterMobile !== undefined)
	{
		if (matchMedia('screen and (orientation: landscape)').matches)
		{
			video.poster = posterDesktop;
		}
		else
		{
			video.poster = posterMobile;
		}
	}

	window.addEventListener('resizeHorizontallyEnd', function () {

		whichSizeVideo(video);

	});
}


/**
 * Scrub video on scroll
 *
 * @see https://codepen.io/shshaw/pen/vYKBPbv/9e810322d70c306de2d18237d0cb2d78
 * @see https://codepen.io/GreenSock/pen/xxrOdpj
 * @see https://gsap.com/community/forums/topic/29356-scrubbing-through-several-successive-pinned-videos-with-scrolltrigger/
 */
/* The encoding is super important here to enable frame-by-frame scrubbing. */

// ffmpeg -i ~/Downloads/Toshiba\ video/original.mov -movflags faststart -vcodec libx264 -crf 23 -g 1 -pix_fmt yuv420p output.mp4
// ffmpeg -i ~/Downloads/Toshiba\ video/original.mov -vf scale=960:-1 -movflags faststart -vcodec libx264 -crf 20 -g 1 -pix_fmt yuv420p output_960.mp4
export function videoScrub(video, vars) {
	video         = gsap.utils.toArray(video)[0]; // in case selector text is fed in.
	let once      = (el, event, fn) => {
		    let onceFn = function () {
			    el.removeEventListener(event, onceFn);
			    fn.apply(this, arguments);
		    };
		    el.addEventListener(event, onceFn);
		    return onceFn;
	    },
	    prepFunc  = () => {
		    video.play();
		    video.pause();
	    },
	    prep      = () => once(document.documentElement, "touchstart", prepFunc),
	    src       = video.currentSrc || video.src,
	    tween     = gsap.fromTo(video, {currentTime: 0}, {
		    paused:          true,
		    immediateRender: false,
		    currentTime:     video.duration || 1,
		    ease:            "none", ...vars
	    }),
	    resetTime = () => (tween.vars.currentTime = video.duration || 1) && tween.invalidate();
	prep();
	video.readyState ? resetTime() : once(video, "loadedmetadata", resetTime);
	return tween;
}



/**
 * Safari browser detection
 * @type {boolean}
 */
export const is_safari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

if (is_safari)
{
	document.body.classList.add('is-safari');
}

/**
 * Instagram browser detection
 * @type {boolean}
 */
let ua           = navigator.userAgent || navigator.vendor || window.opera;
let is_instagram = (ua.indexOf('Instagram') > -1);

if (is_instagram)
{
	document.body.classList.add('is-instagram');
}

export function calculateViewportDimensions() {
	document.documentElement.style.setProperty('--viewport-height', window.visualViewport.height + 'px');
	document.documentElement.style.setProperty('--viewport-width', window.visualViewport.width + 'px');
}

calculateViewportDimensions();

// use to update values on resize end
window.addEventListener('resizeEnd', calculateViewportDimensions);
window.addEventListener('load', calculateViewportDimensions);

// use to update values instantly
/*visualViewport.addEventListener('resize', () => {
	calculateViewportDimensions();
});*/


let prevWindowWidth = window.innerWidth;

window.addEventListener('resize', function (event) {
	if (event.target === window)
	{
		// trigger resizeEnd event only when the window resizing ends
		if (this.resizeTO)
		{
			clearTimeout(this.resizeTO);
		}
		this.resizeTO = setTimeout(function () {
			const event = new CustomEvent('resizeEnd');
			window.dispatchEvent(event);

			const windowWidth = window.innerWidth;

			if (windowWidth !== prevWindowWidth)
			{
				prevWindowWidth = windowWidth;

				const eventH = new CustomEvent('resizeHorizontallyEnd');
				window.dispatchEvent(eventH);
			}
		}, 200);
	}
});